// Sudoku Types
export type CellValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export type SudokuGrid = CellValue[][];
export type Position = { row: number; col: number };

export type Difficulty = 'very_easy' | 'easy' | 'medium' | 'hard' | 'expert';

export const DIFFICULTY_CONFIG: Record<Difficulty, { minClues: number; maxClues: number }> = {
  very_easy: { minClues: 50, maxClues: 55 },
  easy: { minClues: 45, maxClues: 50 },
  medium: { minClues: 35, maxClues: 40 },
  hard: { minClues: 28, maxClues: 32 },
  expert: { minClues: 22, maxClues: 26 },
};

// Game Types
export interface Game {
  id: string;
  userId: number;
  puzzle: SudokuGrid;
  solution: SudokuGrid;
  currentState: SudokuGrid;
  difficulty: Difficulty;
  hintsUsed: number;
  timeElapsed: number;
  status: 'in_progress' | 'completed' | 'abandoned';
  completedAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface GameMove {
  row: number;
  col: number;
  value: CellValue;
  isNote?: boolean;
}

// User Types
export interface User {
  id: number;
  telegramId: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  photoUrl?: string;
  languageCode: string;
  createdAt: number;
  lastSeen: number;
}

// Statistics Types
export interface UserStatistics {
  userId: number;
  totalGames: number;
  completedGames: number;
  abandonedGames: number;
  totalTime: number;
  bestTimeVeryEasy?: number;
  bestTimeEasy?: number;
  bestTimeMedium?: number;
  bestTimeHard?: number;
  bestTimeExpert?: number;
  currentStreak: number;
  longestStreak: number;
  totalHintsUsed: number;
  achievements: Achievement[];
  updatedAt: number;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt: number;
}

// Leaderboard Types
export interface LeaderboardEntry {
  rank: number;
  user: User;
  difficulty: Difficulty;
  bestTime: number;
  gameId: string;
  achievedAt: number;
}

// Daily Challenge Types
export interface DailyChallenge {
  id: number;
  date: string;
  puzzle: SudokuGrid;
  solution: SudokuGrid;
  difficulty: Difficulty;
  totalAttempts: number;
  totalCompletions: number;
  createdAt: number;
}

export interface DailyAttempt {
  challengeId: number;
  userId: number;
  timeElapsed?: number;
  completed: boolean;
  completedAt?: number;
}

// Hint Types
export type HintType = 
  | 'check_errors'
  | 'show_candidates'
  | 'reveal_cell'
  | 'highlight_conflicts'
  | 'smart_hint';

export interface Hint {
  type: HintType;
  position?: Position;
  candidates?: number[];
  value?: CellValue;
  explanation?: string;
}

// Settings Types
export interface UserSettings {
  theme: 'light' | 'dark' | 'auto';
  language: 'en' | 'ru';
  soundEnabled: boolean;
  hapticEnabled: boolean;
  highlightSimilar: boolean;
  showTimer: boolean;
  autoCheckErrors: boolean;
}

// API Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

// Telegram Types
export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

export interface TelegramInitData {
  query_id?: string;
  user?: TelegramUser;
  auth_date: number;
  hash: string;
}


